import { useEffect, useRef } from 'react';
import { apiCall } from '../services/api';
import { toast } from './use-toast';

/**
 * Global Email Auto-Sync Hook
 * Syncs iCloud emails every 30 seconds in the background (even when not on Email Center page)
 * Shows toast notifications for new emails
 * Updates unread counts in sidebar
 */
export const useEmailAutoSync = () => {
  const autoSyncIntervalRef = useRef(null);
  const previousUnreadCountRef = useRef(null);
  const isMountedRef = useRef(true);

  const syncICloudEmails = async () => {
    try {
      // Check if iCloud is connected
      const configResponse = await apiCall('/icloud-email/config?user_id=dwoodson');
      
      if (!configResponse || !configResponse.icloud_email) {
        // iCloud not connected, skip sync
        return;
      }

      // Trigger background sync (non-blocking)
      apiCall('/icloud-email/sync?user_id=dwoodson', {
        method: 'POST'
      }).catch(() => {
        // Silent fail for background sync
      });

      // Fetch emails from database
      const data = await apiCall('/icloud-email/list?user_id=dwoodson&skip=0&limit=100&filter_promotional=true');
      
      if (!data || !data.emails) {
        return;
      }

      // Calculate unread count (only inbox emails)
      const unreadCount = data.emails.filter(e => 
        !e.is_read && 
        e.is_deleted !== true && 
        e.is_archived !== true && 
        e.folder !== 'drafts' && 
        e.folder !== 'sent' && 
        e.folder !== 'sent messages'
      ).length;

      // Update sidebar badge
      window.dispatchEvent(new CustomEvent('emailCountChanged', { detail: { unreadCount } }));
      
      // Notify EmailCenter to refresh if it's open
      window.dispatchEvent(new CustomEvent('emailSyncComplete'));

      // Show notification if new emails detected
      if (previousUnreadCountRef.current !== null && unreadCount > previousUnreadCountRef.current) {
        const newEmailsCount = unreadCount - previousUnreadCountRef.current;
        const newUnreadEmails = data.emails.filter(e => 
          !e.is_read && 
          e.is_deleted !== true && 
          e.is_archived !== true && 
          e.folder !== 'drafts' && 
          e.folder !== 'sent' && 
          e.folder !== 'sent messages'
        ).slice(0, newEmailsCount);

        if (newUnreadEmails.length > 0) {
          const firstEmail = newUnreadEmails[0];
          const fromAddress = firstEmail.from_address || '';
          const emailMatch = fromAddress.match(/^(.+?)\s*<(.+?)>$/);
          const senderName = emailMatch ? emailMatch[1].trim() : fromAddress.split('@')[0];

          toast({
            title: `📧 ${newEmailsCount} New Email${newEmailsCount > 1 ? 's' : ''}`,
            description: newEmailsCount === 1 
              ? `From: ${senderName}\n${firstEmail.subject || '(No Subject)'}` 
              : `You have ${newEmailsCount} new unread emails`,
            duration: 5000
          });
        }
      }

      // Update previous count
      previousUnreadCountRef.current = unreadCount;

      console.log(`🔄 Background email sync: ${data.emails.length} total, ${unreadCount} unread`);
    } catch (error) {
      console.error('❌ Background email sync error:', error);
      // Silent fail - don't disrupt user experience
    }
  };

  useEffect(() => {
    // Initial sync after 10 seconds (give app time to load)
    const initialSyncTimeout = setTimeout(() => {
      syncICloudEmails();
    }, 10000);

    // Set up recurring sync every 5 minutes (300 seconds)
    // This reduces API calls by 90% compared to 30-second interval
    autoSyncIntervalRef.current = setInterval(() => {
      if (isMountedRef.current) {
        syncICloudEmails();
      }
    }, 300000); // 5 minutes (300 seconds)

    return () => {
      isMountedRef.current = false;
      clearTimeout(initialSyncTimeout);
      if (autoSyncIntervalRef.current) {
        clearInterval(autoSyncIntervalRef.current);
        autoSyncIntervalRef.current = null;
      }
      console.log('🔄 Global email auto-sync stopped');
    };
  }, []);

  return null;
};
